import React from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Loader, Stack, useMantineTheme } from '@mantine/core';
import { useRouter } from 'next/router';

import { Routes } from '~/domains/common/constants/routes';
import { Button, ButtonLink } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { TextInput, PasswordInput } from '~/domains/design-system/TextInput';
import dt from '~/testing';

import useStyles from './SignInActions.styles';

type MantinneFormFieldProps = {
  value: any;
  onChange: any;
  checked?: any;
  error?: any;
  onFocus?: any;
  onBlur?: any;
};
export type SignInFormProps = {
  handleFormSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  emailFieldProps: MantinneFormFieldProps;
  passwordFieldProps: MantinneFormFieldProps;
  isFormLoading: boolean;
};

function SignInForm({
  emailFieldProps,
  handleFormSubmit,
  isFormLoading,
  passwordFieldProps,
}: SignInFormProps) {
  const { classes, cx } = useStyles();
  const theme = useMantineTheme();
  const { t } = useTranslation('auth');
  const router = useRouter();

  return (
    <form
      className={classes.formContainer}
      data-test-id={dt.components.shared.auth.signUp.form}
      onSubmit={handleFormSubmit}
    >
      <TextInput
        data-test-id={dt.components.shared.auth.signIn.emailInput}
        errorProps={{
          'data-test-id': dt.components.shared.auth.signIn.emailInputError,
        }}
        label={t('signIn.emailOrUsername.label')}
        w="100%"
        sx={{
          input: {
            backgroundColor: theme.colors.gray[1],
          },
        }}
        {...emailFieldProps}
      />
      <Stack spacing={12}>
        <PasswordInput
          label={t('signIn.password.label')}
          data-test-id={dt.components.shared.auth.passwordInput}
          errorProps={{
            'data-test-id': dt.components.shared.auth.passwordError,
          }}
          w="100%"
          sx={{
            input: {
              backgroundColor: theme.colors.gray[1],
            },
            '& .mantine-PasswordInput-rightSection': {
              backgroundColor: theme.colors.gray[1],
              paddingRight: 16,
            },
          }}
          {...passwordFieldProps}
        />
        <Text variant="body-small" color="gray2.3" className={classes.emailOrUsernameInfo}>
          {t('signIn.emailOrUsername.info')}
        </Text>
      </Stack>

      <Button
        data-test-id={dt.components.shared.auth.signIn.submitButton}
        fullWidth
        variant="primary-fill"
        size="large"
        disabled={isFormLoading}
      >
        {isFormLoading ? (
          <Loader stroke={theme.white} size="sm" sx={{ verticalAlign: 'bottom' }} />
        ) : (
          t('signIn.title')
        )}
      </Button>

      <ButtonLink
        data-test-id={dt.components.shared.auth.signIn.forgotPasswordLink}
        href={Routes.forgotPassword({
          redirectTo: router.query.redirectTo as string,
          brand: router.query.brand as string,
          platform: router.query.platform as string,
        })}
        className={cx(classes.buttonLink, classes.forgotPasswordLink)}
        mode="linkButton"
        size="small"
        type="text"
      >
        {t('forgotPassword.cta')}
      </ButtonLink>
    </form>
  );
}

export default SignInForm;
